import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Text, View } from 'react-native';
import i18n from 'i18next';
import {
  Layout,
  Loader,
  Favorites,
  Button,
  PrivacyPolicyForm,
  OnboardingNavBar,
} from '../../components';
import { SplashScreen } from '../index';
import { useTags } from '../../contexts/Tag/TagContext';
import { NavigationParamList, ScreenName, TabNavigationScreenProps } from '../../navigation/types';
import AnimatedText from '../../components/AnimatedText';
import { isMobile, isNative, isTablet } from '../../utils/responsive';
import client from '../../client/client';
import { useAuth } from '../../contexts/AuthContext/AuthContext';
import { useMessage } from '../../contexts/Messages/MessageContext';
import { Language } from '../../resources/interfaces';

const OnboardingScreen: React.FC<
  TabNavigationScreenProps<NavigationParamList, ScreenName.OnboardingScreen>
> = () => {
  const { tags } = useTags();
  const { t } = useTranslation();
  const [passedTags, setPassedTags] = useState(false);
  const [passedAnimatedText, setPassedAnimatedText] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { fetchMe, me } = useAuth();

  const { setMessage } = useMessage();

  // Screen can be unmounted by the navigator remount triggered when `me`
  // updates. Guard async completions so they don't touch freed React state.
  const isMountedRef = useRef(true);
  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    [],
  );

  useEffect(() => {
    const markOnboarded = async () => {
      try {
        await client.setOnboarded();
        await client.saveUserLanguage(i18n.language as Language).catch((error) => {
          // eslint-disable-next-line no-console
          console.error('save user language error:', error);
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log('error is', error?.response);
        if (error?.response?.status === 422) {
          // eslint-disable-next-line no-console
          console.log('User is already onboarded!');
        } else if (isMountedRef.current) {
          setMessage({ message: error.message, type: 'error' });
        }
      }
    };

    if (me && !me?.isOnboarded) {
      markOnboarded();
    }
  }, [me]);

  const handlePrivacyAccept = async () => {
    setIsSubmitting(true);
    try {
      if (!me?.conditionAccepted) {
        await client.acceptCondition();
      }
      await fetchMe();
    } catch (error) {
      if (isMountedRef.current) {
        setIsSubmitting(false);
        if (error?.response?.status !== 422) {
          setMessage({ message: error.message, type: 'error' });
        }
      }
    }
  };

  const followRequiredTags = async () => {
    const requiredTagLinks = tags
      .filter((t) => t.isRequired)
      .map((t) => ({
        tagId: t.id,
        shouldNotify: true,
      }));

    if (requiredTagLinks.length > 0) {
      try {
        await client.createTag({
          tagLinks: requiredTagLinks,
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Following required tags was not successful', error);
      }
    }
  };

  if (isSubmitting) {
    return <SplashScreen />;
  }

  if (passedTags && passedAnimatedText) {
    return <PrivacyPolicyForm onAccept={handlePrivacyAccept} />;
  }

  if (passedTags) {
    return (
      <View className="bg-neutral-50 dark:bg-neutral-950 flex-1 justify-center">
        <OnboardingNavBar />
        <AnimatedText />
        <View
          style={{
            paddingHorizontal: 30,
            position: isNative ? 'absolute' : ('fixed' as 'absolute'),
            bottom: isNative ? 40 : 80,
            width: '100%',
          }}
        >
          <Button
            text={t('onboarding:next')}
            onPress={() => setPassedAnimatedText(true)}
            variant={'fill'}
            size={'medium'}
            style={{ alignSelf: !isMobile ? 'center' : isTablet ? 'center' : undefined }}
          />
        </View>
      </View>
    );
  }

  return tags ? (
    <View>
      <OnboardingNavBar />
      <Layout
        layoutWithoutMenu={true}
        title={t('onboarding:onboarding_title')}
        isScrollView={isMobile}
      >
        <Text className="text-sectionSubtitleMobile sm:text-sectionSubtitle align-left w-full text-neutral-950 dark:text-neutral-50 mb-10 sm:mb-20 font-[sans-600] max-w-[800px] mt-[-28px]">
          {t('onboarding:onboarding_subtitle')}
        </Text>
        <Favorites />
        <View
          style={{
            flex: 1,
            width: '100%',
          }}
        >
          <Button
            text={t('onboarding:next')}
            onPress={async () => {
              await followRequiredTags();
              setPassedTags(true);
            }}
            variant={'fill'}
            size={'medium'}
            style={{ alignSelf: !isMobile ? 'center' : isTablet ? 'center' : undefined }}
          />
        </View>
      </Layout>
    </View>
  ) : (
    <Loader />
  );
};

export default OnboardingScreen;
